import { useEffect, useState, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'

export default function VerifyPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { verifyMagicLink } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const [isVerifying, setIsVerifying] = useState(true)
  const verificationAttempted = useRef(false)

  useEffect(() => {
    // Prevent double verification (React StrictMode or race conditions)
    if (verificationAttempted.current) return
    verificationAttempted.current = true

    const token = searchParams.get('token')
    const userType = (searchParams.get('type') as 'expert' | 'company') || 'expert'

    if (!token) {
      setError('Invalid magic link. Please request a new one.')
      setIsVerifying(false)
      return
    }

    const verify = async () => {
      try {
        await verifyMagicLink(token, userType)
        navigate('/dashboard')
      } catch (err) {
        console.error('Verification error:', err)
        setError('This magic link is invalid or has expired. Please request a new one.')
      } finally {
        setIsVerifying(false)
      }
    }

    verify()
  }, [searchParams, verifyMagicLink, navigate])

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying your magic link...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Failed</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <Link to="/login" className="btn-primary">
            Request New Link
          </Link>
        </div>
      </div>
    )
  }

  return null
}
